import { useEffect, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion, type PanInfo } from 'framer-motion'
import { cn } from '@/lib/utils'

interface BarMenuItem {
  name: string
  description?: string
  allergens?: string
  price?: string
  image: string
}

interface BarMenuSectionProps {
  id?: string
  title: string
  description?: string
  items: BarMenuItem[]
  variant?: 'light' | 'dark'
  reverse?: boolean
}

export function BarMenuSection({
  id,
  title,
  description,
  items,
  variant = 'light',
  reverse = false,
}: BarMenuSectionProps) {
  const shouldReduceMotion = useReducedMotion()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isHovering, setIsHovering] = useState(false)

  useEffect(() => {
    if (isHovering || items.length === 0) return
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length)
    }, 4200)
    return () => clearInterval(interval)
  }, [isHovering, items.length])

  const activeItem = items[activeIndex]

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const handleActivate = (index: number) => {
    setIsHovering(true)
    setActiveIndex(index)
  }

  const handleDeactivate = () => setIsHovering(false)

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const offsetThreshold = 50
    const velocityThreshold = 500
    if (info.offset.x > offsetThreshold || info.velocity.x > velocityThreshold) {
      setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1))
    } else if (info.offset.x < -offsetThreshold || info.velocity.x < -velocityThreshold) {
      setActiveIndex((prev) => (prev + 1) % items.length)
    }
  }

  const isLight = variant === 'light'

  return (
    <section
      id={id}
      className={cn(
        'px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8',
        isLight ? 'bg-bar-light' : 'bg-bar'
      )}
      aria-labelledby={id ? `${id}-heading` : undefined}
    >
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-12 text-center sm:mb-16">
          <h2
            id={id ? `${id}-heading` : undefined}
            className="font-display text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold leading-[0.95] tracking-tighter text-ink"
          >
            {title}
          </h2>
          {description && (
            <div className="mx-auto mt-6 max-w-2xl space-y-2 text-lg text-ink/80">
              {description
                .replace(/\\n/g, '\n')
                .split('\n')
                .filter((line) => line.trim() !== '')
                .map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
            </div>
          )}
        </motion.div>

        <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Featured image */}
          <motion.div
            {...fadeUp}
            className={cn(
              'lg:col-span-5',
              reverse ? 'order-1 lg:order-2' : 'order-1 lg:order-1'
            )}
          >
            <div className="lg:sticky lg:top-28">
              <div className="relative mx-auto max-w-sm lg:max-w-none">
                <div
                  className={cn(
                    'absolute inset-0 rotate-2 rounded-[2rem]',
                    isLight ? 'bg-bar-dark' : 'bg-ink'
                  )}
                  aria-hidden="true"
                />
                <div className="relative -rotate-1 rounded-[2rem] border-[6px] border-white bg-white p-2 shadow-xl">
                  <motion.div
                    className="relative aspect-[4/5] w-full overflow-hidden rounded-[1.5rem] bg-silver"
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.2}
                    onDragStart={() => setIsHovering(true)}
                    onDragEnd={(e, info) => {
                      handleDragEnd(e, info)
                      setIsHovering(false)
                    }}
                  >
                    {activeItem?.image && (
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={activeItem.image}
                          src={activeItem.image}
                          alt={activeItem.name}
                          initial={{ opacity: 0, scale: 1.05 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: shouldReduceMotion ? 0 : 0.4 }}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </AnimatePresence>
                    )}
                  </motion.div>
                </div>
              </div>

              {/* Dots */}
              <div className="mt-5 flex justify-center gap-2">
                {items.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={item.name}
                    aria-current={index === activeIndex ? 'true' : undefined}
                    onClick={() => handleActivate(index)}
                    onMouseEnter={() => handleActivate(index)}
                    onMouseLeave={handleDeactivate}
                    className={cn(
                      'h-2.5 w-2.5 rounded-full transition-colors',
                      index === activeIndex
                        ? isLight
                          ? 'bg-bar-dark'
                          : 'bg-ink'
                        : isLight
                          ? 'bg-bar-dark/30 hover:bg-bar-dark/50'
                          : 'bg-ink/30 hover:bg-ink/50'
                    )}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Menu cards */}
          <motion.div
            {...fadeUp}
            className={cn(
              'lg:col-span-7',
              reverse ? 'order-2 lg:order-1' : 'order-2 lg:order-2'
            )}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map((item, index) => {
                const isActive = activeIndex === index
                return (
                  <button
                    key={item.name}
                    type="button"
                    onMouseEnter={() => handleActivate(index)}
                    onMouseLeave={handleDeactivate}
                    onFocus={() => handleActivate(index)}
                    onBlur={handleDeactivate}
                    onClick={() => handleActivate(index)}
                    className={cn(
                      'group relative w-full rounded-xl border-l-4 bg-white p-4 text-left shadow-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:p-5',
                      isLight
                        ? 'focus-visible:ring-bar-dark focus-visible:ring-offset-bar-light'
                        : 'focus-visible:ring-ink focus-visible:ring-offset-bar',
                      isActive
                        ? isLight
                          ? 'border-bar-dark shadow-md'
                          : 'border-ink shadow-md'
                        : isLight
                          ? 'border-cord hover:border-bar-dark/60 hover:shadow-md'
                          : 'border-white/40 hover:border-ink/60 hover:shadow-md'
                    )}
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-display text-lg font-semibold leading-tight tracking-tight text-ink sm:text-xl">
                        {item.name}
                      </span>
                      {item.price && (
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-2.5 py-1 font-display text-xs font-bold text-white',
                            isLight ? 'bg-bar-dark' : 'bg-ink'
                          )}
                        >
                          {item.price}
                        </span>
                      )}
                    </span>
                    {item.description && (
                      <span className="mt-1 block text-sm leading-relaxed text-muted">
                        {item.description}
                      </span>
                    )}
                    {item.allergens && (
                      <span className="mt-2 block text-xs italic text-muted/80">
                        {item.allergens}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
